/**
 * openWineDrawer.js — Shared trigger for the wine tasting / food-pairings drawer.
 *
 * Wine cards call this from their "Tasting notes" button. Image click still
 * routes to the chat detail view (ws-prompt-select) — parity with old widgets.
 */

import { emit, getState } from "../core/state.js";
import { getFeature, getUiFeature } from "../core/configAccessors.js";

/**
 * @param {object} wine       Feature-card payload from the carousel item
 * @param {HTMLElement} [cardEl]  Rendered card root (for listing-level task_id)
 */
export function openWineDrawer(wine, cardEl) {
  const listing = cardEl?.closest?.(".ws-wine-listing");
  const variantId = String(wine.variant_id ?? wine["Variant SKU"] ?? "");
  // Prefer data-cta-link on the card (Hestan/Goose/Halleck). Fall back to
  // wine.cta_link for external-shop tenants without an in-widget cart so the
  // drawer footer still gets Buy Now + Close (renderFooterCTA).
  const fromCard = cardEl?.dataset?.ctaLink || "";
  const wineCtaLink =
    fromCard ||
    (!getUiFeature("show_cart", false) ? wine.cta_link || "" : "") ||
    "";
  // Commerce7 cart lines are keyed by sku (cart_c7.js), not productVariantId —
  // same underlying identifier value, just a different field name on the line.
  const cartLineKey = getFeature("commerce7") ? "sku" : "productVariantId";
  const cartLine = (getState().cart?.items || []).find(
    (it) => String(it[cartLineKey]) === variantId,
  );

  // Commerce7 wine cards pin their own in-widget cart button to "Add to Cart"
  // regardless of wine.cta_text (their feature-card cta_text is "Buy Now",
  // meant for the external CTA link, not the in-chat add button — see
  // WineCard.js's own override for LR/Valle). Match that here so the drawer's
  // button doesn't show "Buy Now" for a purely in-chat add-to-cart action.
  const addToCartLabel = getFeature("commerce7")
    ? "Add to Cart"
    : wine.cta_text || "Add to Basket";

  emit("ws-wine-drawer-open", {
    uniqueId: wine.unique_id,
    wineKey: String(wine.wine_key || wine.variant_id || wine["Variant SKU"] || ""),
    variantId,
    foodPairingsTaskId:
      listing?.dataset?.foodPairingsTaskId ||
      getState().session?.foodPairingsTaskId ||
      "",
    addToCartLabel,
    initialQty: cartLine ? Number(cartLine.quantity) || 0 : 0,
    ctaLink: wineCtaLink,
  });
}

/** @param {HTMLElement} btn @param {object} wine @param {HTMLElement} card */
export function bindWineDrawerOpen(btn, wine, card) {
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    openWineDrawer(wine, card);
  });
}
